import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Header from './components/header';
import GridNav from './components/GridNav';
import ContentDisplay from './components/ContentDisplay';
import CareTeam from './components/CareTeam';
import SearchBar from './components/SearchBar';
import SearchResults from './components/SearchResults';
import Footer from './components/Footer'; // Import the Footer component
import { fetchGroups, fetchAllTopicsAndSubtopics, buildContentTree } from './model/ContentTiles';
// eslint-disable-next-line no-unused-vars
import { initializeFhirClient, fetchCareTeam } from './services/fhirService';
import { onSearch, onChangePage, getTotalPages, onReturnToSearch } from './model/search';
import { getCurrentPage } from './model/search';
// Import all Material UI icons as MuiIcons for dynamic usage
import * as MuiIcons from '@mui/icons-material';

const App = () => {
  // eslint-disable-next-line no-unused-vars
  const { t, i18n } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const [fhirClient, setFhirClient] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [careTeam, setCareTeam] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [isLanguageSet, setIsLanguageSet] = useState(false);
  const [contentTree, setContentTree] = useState(null); // ContentTree | null
  const [navItems, setNavItems] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubTopic, setSelectedSubTopic] = useState(null);
  const [navLoading, setNavLoading] = useState(true);
  const [navError, setNavError] = useState(null);
  const [searchResults, setSearchResults] = useState(null); // null means no search attempted
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // Track if user arrived at subtopic via search
  const [arrivedFromSearch, setArrivedFromSearch] = useState(false);
  // Track the last search term
  const [lastSearchTerm, setLastSearchTerm] = useState("");

  // Track the current value in the search bar
  const [searchInput, setSearchInput] = useState("");

  // Track if we've pushed the initial state to history
  const initialStatePushed = useRef(false);

  // Initialize i18n with the default language
  // useEffect(() => {
  //   const language = navigator.language || 'en';
  //   i18n.changeLanguage(language);
  //   setIsLanguageSet(true);
  // }, [i18n]);

  // Initialize FHIR automatically as part of the app load
  
  useEffect(() => {
    setLoading(true);
    initializeFhirClient()
      .then((client) => {
        if (!client) {
          //Waiting for OAuth2 redirect
          setLoading(false);
          return;
        }
        setFhirClient(client);
        fetchCareTeam(client)
          .then((data) => {
            setCareTeam(data.careTeam);
            setLoading(false);
            const preferredLanguage = data.preferredLanguage;
            if(preferredLanguage){
                i18n.changeLanguage(preferredLanguage);
            } else {
                i18n.changeLanguage(navigator.language);
            }
            setIsLanguageSet(true);
          })
          .catch((err) => {
            setError(err.message);
            setLoading(false);
          });
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);
  

  //Fetch groups/topics/subtopics based on selection
  useEffect(() => {
    let isMounted = true;
    setNavLoading(true);
    setNavError(null);
    fetchGroups(i18n.language)
      .then((groups) => {
        if (!isMounted) return;
        setNavItems(groups); // Show groups immediately
        setNavLoading(false);
        // Background: fetch all topics/subtopics and build tree
        fetchAllTopicsAndSubtopics(i18n.language)
          .then(({ topics, subtopics }) => {
            if (!isMounted) return;
            const tree = buildContentTree(groups, topics, subtopics);
            setContentTree(tree);
          })
          .catch(() => { });
      })
      .catch((err) => {
        if (!isMounted) return;
        setNavError(err.message || 'Failed to load navigation items.');
        setNavLoading(false);
      });
    return () => { isMounted = false; };
  }, [i18n.language]);


  // Push state to history on navigation
  useEffect(() => {
    // Don't push on initial load
    if (!isLanguageSet || navLoading) return;
    const state = { selectedGroup, selectedTopic, selectedSubTopic, searchResults, lastSearchTerm, arrivedFromSearch, searchInput };
    window.history.replaceState(state, ''); // Always sync current state
    // On first eligible render, push initial state so browser back works
    if (!initialStatePushed.current) {
      window.history.pushState(state, '');
      initialStatePushed.current = true;
    }
  }, [isLanguageSet, navLoading, selectedGroup, selectedTopic, selectedSubTopic, searchResults, lastSearchTerm, arrivedFromSearch, searchInput]);

  // Enhanced pushHistory for search and subtopic selection
  const pushHistory = (group, topic, subtopic, options = {}) => {
    // options: { isSearch, isSubtopicFromSearch, prevNavState }
    const state = {
      selectedGroup: group,
      selectedTopic: topic,
      selectedSubTopic: subtopic,
      searchResults: options.isSearch ? searchResults : null,
      lastSearchTerm: options.isSearch || options.isSubtopicFromSearch ? lastSearchTerm : '',
      arrivedFromSearch: options.isSubtopicFromSearch ? true : false,
      searchInput: options.isSearch ? lastSearchTerm : '',
      prevNavState: options.prevNavState || null
    };
    window.history.pushState(state, '');
  };

  // Handle browser history for navigation
  useEffect(() => {
    const onPopState = (event) => {
      const state = event.state;
      if (!state) {
        // No state: reset to top-level
        setSelectedGroup(null);
        setSelectedTopic(null);
        setSelectedSubTopic(null);
        setSearchResults(null);
        setArrivedFromSearch(false);
        setLastSearchTerm("");
        setSearchInput("");
        if (contentTree) setNavItems(contentTree.groups);
        return;
      }
      setSelectedGroup(state.selectedGroup || null);
      setSelectedTopic(state.selectedTopic || null);
      setSelectedSubTopic(state.selectedSubTopic || null);
      setArrivedFromSearch(state.arrivedFromSearch || false);
      setLastSearchTerm(state.lastSearchTerm || "");
      setSearchInput(state.searchInput || "");
      if (state.searchResults !== undefined) setSearchResults(state.searchResults);
      // If returning to search from subtopic, re-run search if needed
      if (state.lastSearchTerm && state.searchResults === null && !state.selectedSubTopic) {
        setSearchLoading(true);
        setSearchError(null);
        onSearch(state.lastSearchTerm, i18n.language).then(results => {
          setSearchResults(results);
          setTotalPages(getTotalPages());
          setCurrentPage(0);
          setSearchLoading(false);
        }).catch(() => {
          setSearchResults([]);
          setTotalPages(1);
          setCurrentPage(0);
          setSearchLoading(false);
        });
      } else if (state.searchResults && !state.selectedSubTopic) {
        // If restoring search results, update pagination
        setTotalPages(getTotalPages());
        setCurrentPage(window.__lastSearchPage || 0);
      }
      // Set navItems for the current state
      if (contentTree) {
        if (!state.selectedGroup) {
          setNavItems(contentTree.groups);
        } else if (!state.selectedTopic) {
          const group = contentTree.groups.find(g => g.id === state.selectedGroup.id);
          setNavItems(group ? group.topics : []);
        } else if (!state.selectedSubTopic) {
          const topic = contentTree.topics.find(t => t.id === state.selectedTopic.id);
          setNavItems(topic ? topic.subtopics : []);
        } else {
          setNavItems([]);
        }
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [contentTree, i18n.language]);

  // Navigation logic using in-memory tree
  // Handles tile selection and updates navigation state
  const handleNavItemClick = (item) => {
    if (!contentTree) return;
    setArrivedFromSearch(false); // Manual nav, not from search
    if (!selectedGroup) {
      setSelectedGroup(item);
      // Show topics for this group
      const group = contentTree.groups.find(g => g.id === item.id);
      setNavItems(group ? group.topics : []);
      setSelectedTopic(null);
      setSelectedSubTopic(null);
      pushHistory(item, null, null); // Push state to browser history
    } else if (!selectedTopic) {
      setSelectedTopic(item);
      // Show subtopics for this topic
      const topic = contentTree.topics.find(t => t.id === item.id);
      setNavItems(topic ? topic.subtopics : []);
      setSelectedSubTopic(null);
      pushHistory(selectedGroup, item, null); // Push state to browser history
    } else if (!selectedSubTopic) {
      setSelectedSubTopic(item);
      // No further navItems to show
      setNavItems([]);
      pushHistory(selectedGroup, selectedTopic, item); // Push state to browser history
    }
  };

  // Breadcrumb navigation using in-memory tree
  // Handles navigation via breadcrumb links and updates browser history
  const handleBackToGroups = () => {
    setSelectedGroup(null);
    setSelectedTopic(null);
    setSelectedSubTopic(null);
    setSearchResults(null); // Clear search results when returning Home
    setArrivedFromSearch(false);
    setSearchInput(""); // Clear search bar
    setCurrentPage(0);
    setTotalPages(1);
    if (contentTree) setNavItems(contentTree.groups);
    pushHistory(null, null, null); // Push state to browser history
  };
  const handleBackToTopics = () => {
    setSelectedTopic(null);
    setSelectedSubTopic(null);
    setArrivedFromSearch(false);
    setSearchInput(""); // Clear search bar
    if (contentTree && selectedGroup) {
      const group = contentTree.groups.find(g => g.id === selectedGroup.id);
      setNavItems(group ? group.topics : []);
    }
    pushHistory(selectedGroup, null, null); // Push state to browser history
  };
  const handleBackToSubTopics = () => {
    setSelectedSubTopic(null);
    setArrivedFromSearch(false);
    setSearchInput(""); // Clear search bar
    if (contentTree && selectedTopic) {
      const topic = contentTree.topics.find(t => t.id === selectedTopic.id);
      setNavItems(topic ? topic.subtopics : []);
    }
    pushHistory(selectedGroup, selectedTopic, null); // Push state to browser history
  };

  const buildIconDom = (IconComponent, backGroundColor, iconSize) => {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: backGroundColor, // fixed: pass as string, not object
          borderRadius: '50%',
          width: 100,
          height: 100,
        }}
      >
        <IconComponent style={{ color: '#fff', fontSize: iconSize }} />
      </span>
    );
  }
  // Optionally provide icon and topic count
  const getIcon = (item) => {//parentGroupId
    if (item.id.includes('6830d6394482600002ac28f8')) return buildIconDom(MuiIcons.Psychology, '#98c454', '75');
    if (item.id.includes('6830d62b4482600002ac28f7')) return buildIconDom(MuiIcons.Groups, '#10A3F6', '75');
    if (item.id.includes('6830d6494482600002ac28f9')) return buildIconDom(MuiIcons.Public, 'orange', '75');
    if (item.id.includes('683606dfece9e90002260df8')) return buildIconDom(MuiIcons.Forum, '#e03c64', '75');
    if (item.id.includes('683606a2ece9e90002260df5')) return buildIconDom(MuiIcons.Coronavirus, '#48a4f4', '75');
    if (item.id.includes('683606b4ece9e90002260df6')) return buildIconDom(MuiIcons.Medication, '#E000A1', '75');
    if (item.id.includes('683606ccece9e90002260df7')) return buildIconDom(MuiIcons.AccessibilityNew, '#E30000', '75');
    if (item.id.includes('683606f7ece9e90002260df9')) return buildIconDom(MuiIcons.SupportAgent, '#68ac54', '75');

    if (item.parentGroupId) {
      if (item.parentGroupId.includes('6830d6394482600002ac28f8')) return buildIconDom(MuiIcons.Psychology, '#98c454', '75');
      if (item.parentGroupId.includes('6830d62b4482600002ac28f7')) return buildIconDom(MuiIcons.Groups, '#10A3F6', '75');
      if (item.parentGroupId.includes('6830d6494482600002ac28f9')) return buildIconDom(MuiIcons.Public, 'orange', '75');
      if (item.parentGroupId.includes('683606dfece9e90002260df8')) return buildIconDom(MuiIcons.Forum, '#e03c64', '75');
      if (item.parentGroupId.includes('683606a2ece9e90002260df5')) return buildIconDom(MuiIcons.Coronavirus, '#48a4f4', '75');
      if (item.parentGroupId.includes('683606b4ece9e90002260df6')) return buildIconDom(MuiIcons.Medication, '#E000A1', '75');
      if (item.parentGroupId.includes('683606ccece9e90002260df7')) return buildIconDom(MuiIcons.AccessibilityNew, '#E30000', '75');
      if (item.parentGroupId.includes('683606f7ece9e90002260df9')) return buildIconDom(MuiIcons.SupportAgent, '#68ac54', '75');
    }

    return <span role="img" aria-label="Module"></span>;
  };
  // Show topic count for groups, subtopic count for topics, and no count for subtopics
  const getTopicCount = (item) => {
    // If it's a Group, show the number of topics
    if (item.topics && Array.isArray(item.topics)) {
      return item.topics.length;
    }
    // If it's a Topic, show the number of subtopics
    if (item.subtopics && Array.isArray(item.subtopics)) {
      return item.subtopics.length;
    }
    // If it's a Subtopic (leaf), show no counter
    return undefined;
  };

  // Handler for search bar
  const handleSearch = async (query) => {
    setSearchLoading(true);
    setSearchError(null);
    setLastSearchTerm(query); // Remember the search term
    setSearchInput(query); // Keep input in sync
    setCurrentPage(0); // Reset to first page on new search
    setSelectedGroup(null);
    setSelectedTopic(null);
    setSelectedSubTopic(null);
    setSearchResults([]); // <--- Show SearchResults immediately with loading state
    // Save previous nav state for back/forward
    const prevNavState = { selectedGroup, selectedTopic, selectedSubTopic };
    try {
      if (!initialStatePushed.current) {
        const state = {
          selectedGroup,
          selectedTopic,
          selectedSubTopic,
          searchResults: null,
          lastSearchTerm: '',
          arrivedFromSearch: false,
          searchInput: '',
          prevNavState: null
        };
        window.history.replaceState(state, '');
        initialStatePushed.current = true;
      }
      const results = await onSearch(query, i18n.language);
      setSearchResults(results);
      // Immediately update pagination state after search
      setTotalPages(getTotalPages());
      setCurrentPage(getCurrentPage());
      pushHistory(null, null, null, { isSearch: true, prevNavState });
    } catch {
      setSearchError('Failed to fetch search results.');
    } finally {
      setSearchLoading(false);
    }
  };

  // Handler for changing search result page
  const handleChangePage = async (page) => {
    if (page === currentPage) return;
    setSearchLoading(true);
    setSearchError(null);
    try {
      const results = await onChangePage(page);
      setSearchResults(results);
      setTotalPages(getTotalPages());
      setCurrentPage(page);
    } catch {
      setSearchError('Failed to fetch search results.');
    } finally {
      setSearchLoading(false);
    }
  };

  // Handler for selecting a search result
  const handleSelectSearchResult = (subtopicId) => {
    if (!contentTree) return;
    // Find the subtopic, topic, and group in the tree
    const subtopic = contentTree.subtopics.find(st => st.id === subtopicId);
    if (!subtopic) return;
    const topic = contentTree.topics.find(t => t.id === subtopic.parentTopicId);
    const group = topic ? contentTree.groups.find(g => g.id === topic.parentGroupId) : null;
    setSelectedGroup(group || null);
    setSelectedTopic(topic || null);
    setSelectedSubTopic(subtopic);
    setSearchResults(null); // Clear search results after selection
    setArrivedFromSearch(true); // Mark that we arrived via search
    // Push state for subtopic-from-search, so back returns to search
    pushHistory(group, topic, subtopic, { isSubtopicFromSearch: true });
  };

  // Handler for returning to search from subtopic/article
  const handleReturnToSearch = async () => {
    setSelectedSubTopic(null);
    setSelectedTopic(null);
    setSelectedGroup(null);
    setArrivedFromSearch(false);
    setSearchLoading(true);
    setSearchError(null);
    try {
      const results = await onReturnToSearch();
      setSearchResults(results);
      setTotalPages(getTotalPages());
      setCurrentPage(getCurrentPage()); // Use getter to sync with model
    } catch {
      setSearchError('Failed to fetch search results.');
    } finally {
      setSearchLoading(false);
    }
  };

  // Always update totalPages and currentPage when searchResults changes and is not null
  React.useEffect(() => {
    if (searchResults !== null) {
      setTotalPages(getTotalPages());
      setCurrentPage(getCurrentPage());
    }
  }, [searchResults]);

  return (
    <>
      {isLanguageSet && (
        <div className="min-h-screen w-screen flex flex-col min-w-0" style={{ background: '#ddd7f1' }}>
          <Header />
          {/* Search Bar: show only if not viewing a SubTopic */}
          {!selectedSubTopic && (
            <div className="w-full max-w-xl mx-auto pt-1 pb-1" style={{ paddingTop: 3, paddingBottom: 3 }}>
              <SearchBar onSearch={handleSearch} value={searchInput} onChange={setSearchInput} />
            </div>
          )}
          <div className="flex-1 w-full flex flex-col items-center min-w-0">
            <div className="w-full max-w-7xl px-4 flex-1 flex flex-col min-h-[600px] min-w-0"> 
              {/* Back to Search Results link and Breadcrumb/Title alignment wrapper */}
              <div className="w-full max-w-7xl px-1">
                {/* Back to Search Results link: only show if we navigated here from search */}
                {selectedSubTopic && selectedGroup && selectedTopic && arrivedFromSearch && (
                  <div className="mb-2">
                    <button
                      className="breadcrumb text-primary font-medium bg-transparent hover:text-[#6C2BD7] focus:text-[#6C2BD7] rounded px-2 py-1 transition-colors focus:outline-none"
                      style={{ boxShadow: 'none', border: 'none', textDecoration: 'underline transparent', paddingLeft: 0 }}
                      onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline #6C2BD7'}
                      onMouseLeave={e => e.currentTarget.style.textDecoration = 'underline transparent'}
                      onClick={handleReturnToSearch}
                      aria-label="Back to Search Results"
                      type="button"
                    >
                      ← Back to Search Results
                    </button>
                  </div>
                )}
                {/* Breadcrumb Trail (always rendered, visually hidden on home) */}
                <div className="w-full max-w-7xl mx-auto">
                  <nav
                    className={`breadcrumb flex items-center text-muted-foreground mb-2 transition-opacity duration-100 ${!(selectedGroup || selectedTopic || selectedSubTopic || (searchResults !== null && !selectedSubTopic)) ? 'opacity-0 pointer-events-none select-none' : 'opacity-100'}`}
                    aria-label="Breadcrumb"
                    style={{ minHeight: '2.5rem' }}
                  >
                    <ol className="flex items-center w-full">
                      <li className="ml-0">
                        <span
                          onClick={selectedGroup || selectedTopic || selectedSubTopic || (searchResults !== null && !selectedSubTopic) ? handleBackToGroups : undefined}
                          className={`cursor-pointer font-medium flex items-center gap-1 transition-colors breadcrumb-hover-purple ${!(selectedGroup || selectedTopic || selectedSubTopic || (searchResults !== null && !selectedSubTopic)) ? 'text-primary pointer-events-none' : ''}`}
                          aria-current={!(selectedGroup || selectedTopic || selectedSubTopic || (searchResults !== null && !selectedSubTopic)) ? 'page' : undefined}
                          tabIndex={(selectedGroup || selectedTopic || selectedSubTopic || (searchResults !== null && !selectedSubTopic)) ? 0 : -1}
                          role={(selectedGroup || selectedTopic || selectedSubTopic || (searchResults !== null && !selectedSubTopic)) ? 'link' : undefined}
                        >
                          <MuiIcons.HomeOutlined style={{ fontSize: 18, marginRight: 4, minWidth: '1rem', minHeight: '1rem' }} aria-hidden="true" />
                          Home
                        </span>
                      </li>
                      {/* Only render a single '>' after Home if there are more entries */}
                      {(selectedGroup || selectedTopic || selectedSubTopic || (searchResults !== null && !selectedSubTopic)) && (
                        <li><span className="mx-2 text-gray-400">&gt;</span></li>
                      )}
                      {/* Show Search Results breadcrumb if search is active and not viewing a subtopic */}
                      {(searchResults !== null && !selectedSubTopic) && (
                        <li><span className="bc-last-entry" aria-current="page">Search Results</span></li>
                      )}
                      {/* Otherwise, show normal breadcrumb for group/topic/subtopic */}
                      {selectedGroup && !selectedTopic && !selectedSubTopic && !searchResults && (
                        <li>
                          <span className="bc-last-entry" aria-current="page">{selectedGroup.title}</span>
                        </li>
                      )}
                      {selectedGroup && selectedTopic && !selectedSubTopic && !searchResults && (
                        <>
                          <li>
                            <span
                              onClick={handleBackToTopics}
                              className="cursor-pointer font-medium transition-colors breadcrumb-hover-purple"
                              tabIndex={0}
                              role="link"
                            >
                              {selectedGroup.title}
                            </span>
                          </li>
                          <li><span className="mx-2 text-gray-400">&gt;</span></li>
                          <li>
                            <span className="bc-last-entry" aria-current="page">{selectedTopic.title}</span>
                          </li>
                        </>
                      )}
                      {selectedGroup && selectedTopic && selectedSubTopic && !searchResults && (
                        <>
                          <li>
                            <span
                              onClick={handleBackToTopics}
                              className="cursor-pointer font-medium transition-colors breadcrumb-hover-purple"
                              tabIndex={0}
                              role="link"
                            >
                              {selectedGroup.title}
                            </span>
                          </li>
                          <li><span className="mx-2 text-gray-400">&gt;</span></li>
                          <li>
                            <span
                              onClick={handleBackToSubTopics}
                              className="cursor-pointer font-medium transition-colors breadcrumb-hover-purple"
                              tabIndex={0}
                              role="link"
                            >
                              {selectedTopic.title}
                            </span>
                          </li>
                          <li><span className="mx-2 text-gray-400">&gt;</span></li>
                          <li>
                            <span className="bc-last-entry" aria-current="page">{selectedSubTopic.title}</span>
                          </li>
                        </>
                      )}
                    </ol>
                  </nav>
                </div>
                {/* Grid Title */}
                <div className="mb-3">
                  <h2
                    className="text-2xl font-semibold font-sans"
                    style={{ color: '#3C0C57', fontSize: '2rem' }}
                  >
                    {(!selectedGroup && !selectedTopic && !selectedSubTopic && searchResults === null) && 'Dementia Resources'}
                    {selectedGroup && !selectedTopic && !selectedSubTopic && searchResults === null && selectedGroup.title}
                    {selectedTopic && !selectedSubTopic && searchResults === null && selectedTopic.title}
                    {selectedSubTopic && searchResults === null && selectedSubTopic.title}
                    {/* Show 'Search Results' title if searchResults are present and not viewing a subtopic */}
                    {searchResults !== null && !selectedSubTopic && 'Search Results'}
                  </h2>
                </div>
              </div>
              {/* Only show GridNav if not viewing a SubTopic and not showing search results */}
              <div className="flex-1 w-full flex flex-col min-h-[400px] min-w-0">
                {(!selectedSubTopic && searchResults === null) && (
                  navLoading ? (
                    <div className="flex-1 flex items-center justify-center w-full min-h-[400px] min-w-0">Loading...</div>
                  ) : navError ? (
                    <div className="flex-1 flex items-center justify-center text-red-600 w-full min-h-[400px] min-w-0">{navError}</div>
                  ) : (
                    <GridNav
                      items={navItems}
                      onItemClick={handleNavItemClick}
                      getIcon={getIcon}
                      getTopicCount={getTopicCount}
                    />
                  )
                )}
                {/* Show search results if present and not viewing a SubTopic */}
                {!selectedSubTopic && searchResults !== null && (
                  <div className="w-full max-w-3xl mx-auto mt-4">
                    <SearchResults
                      results={searchResults}
                      loading={searchLoading}
                      error={searchError}
                      onSelectResult={handleSelectSearchResult}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onChangePage={handleChangePage}
                    />
                  </div>
                )}
                <main>
                  {selectedSubTopic ? (
                    <ContentDisplay subTopicId={selectedSubTopic.id} />
                  ) : (
                    null
                  )}
                </main>
              </div>
            </div>    
          </div>                  
          <Footer />
        </div>
      )}
    </>
  );
};

export default App;